import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { 
  Bell, 
  Menu, 
  X,
  BookOpen,
  GraduationCap,
  PlayCircle,
  MessageSquare,
  Calendar,
  User,
  Loader2
} from 'lucide-react';
import { navItems } from '../config/navigation';
import UserProfile from './UserProfile';
import SearchBar from './SearchBar';
import { supabase } from '../lib/supabaseClient';

// Navbar component with notification handling
const Navbar = ({ isMobileMenuOpen, setMobileMenuOpen, user, onLogout }) => (
  <nav className="bg-white border-b border-gray-200 fixed w-full z-30 top-0">
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between h-16">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 hover:bg-gray-100 rounded-lg"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          
          <Link to="/dashboard" className="flex items-center gap-3">
            <BookOpen className="text-navy-600" size={32} />
            <span className="text-xl font-bold text-navy-900">Teampace Sales Academy</span>
          </Link>
          
          <div className="hidden md:block ml-4">
            <SearchBar />
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button 
            className="p-2 hover:bg-gray-100 rounded-lg relative"
            aria-label="Notifications"
          >
            <Bell size={20} className="text-gray-600" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
          </button>
          <UserProfile user={user} onLogout={onLogout} />
        </div>
      </div>
    </div>
  </nav>
);

// Enhanced NavItem component with better type checking
const NavItem = ({ item, isActive, onClick }) => {
  const IconComponent = {
    BookOpen,
    GraduationCap,
    PlayCircle,
    MessageSquare,
    Calendar,
    User
  }[item.icon] || User;  // Fallback to User icon if invalid icon name

  return (
    <Link
      to={item.path}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
        isActive
          ? 'bg-navy-600 text-white' 
          : 'text-gray-600 hover:bg-gray-50'
      }`}
      onClick={onClick}
    >
      <IconComponent size={20} />
      <span className="font-medium">{item.label}</span>
    </Link>
  );
};

// Enhanced Sidebar with improved animation handling
const Sidebar = ({ isOpen, onClose, pathname }) => (
  <>
    {/* Backdrop for mobile */}
    <div 
      className={`fixed inset-0 bg-black transition-opacity duration-300 md:hidden ${
        isOpen ? 'opacity-50 z-40' : 'opacity-0 pointer-events-none'
      }`}
      onClick={onClose}
      aria-hidden="true"
    />
    
    <aside 
      className={`fixed w-64 bg-white border-r border-gray-200 top-16 bottom-0 flex flex-col transform 
        transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      }`}
      aria-label="Sidebar navigation"
    >
      <div className="flex-1 overflow-y-auto p-4 space-y-1">
        {navItems
          .filter(item => !item.bottom)
          .map((item) => (
            <NavItem
              key={item.label}
              item={item}
              isActive={pathname === item.path}
              onClick={onClose}
            />
          ))}
      </div>

      <div className="p-4 border-t border-gray-200 mt-auto">
        {navItems
          .filter(item => item.bottom)
          .map((item) => (
            <NavItem
              key={item.label}
              item={item}
              isActive={pathname === item.path}
              onClick={onClose}
            />
          ))}
      </div>
    </aside>
  </>
);

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Close mobile menu on route change
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
        
        if (authError) throw authError;
        
        if (!authUser) {
          setUser(null);
          setLoading(false);
          navigate('/login');
          return;
        }

        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', authUser.id)
          .single();
          
        if (profileError) throw profileError;

        // Verify avatar URL if it exists
        let verifiedAvatarUrl = null;
        if (profile?.avatar_url) {
          try {
            const { data: existsData, error: existsError } = await supabase.storage
              .from('avatars')
              .createSignedUrl(profile.avatar_url.split('avatars/')[1], 10);

            if (!existsError && existsData) {
              verifiedAvatarUrl = profile.avatar_url;
            }
          } catch (err) {
            console.error('Error verifying avatar:', err);
          }
        }
            
        setUser({
          ...authUser,
          name: profile?.full_name || authUser.email,
          avatar: verifiedAvatarUrl,
          profile: profile
        });
      } catch (error) {
        console.error('Error fetching user profile:', error);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();

    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN') {
        fetchUserProfile();
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
        navigate('/login');
      }
    });

    return () => {
      authListener?.unsubscribe?.();
    };
  }, [navigate]);

  const handleLogout = async () => {
    try {
      setLoading(true);
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-navy-600 animate-spin" />
      </div>
    );
  }

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar 
        isMobileMenuOpen={isMobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        user={user}
        onLogout={handleLogout}
      />
      
      <Sidebar 
        isOpen={isMobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        pathname={location.pathname}
      />

      <main className="md:pl-64 pt-16">
        <div className="p-4 md:p-8">
          <div className="md:hidden mb-4">
            <SearchBar />
          </div>
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;